import React, { useMemo, useState } from 'react';
import { DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { PayrollRun } from '../../../types/hr';
import { employeeName, formatDate, formatKwacha, formatPeriod, maskAccount, sumKwacha } from '../../../utils/hrFormat';
import { exportCsv, exportExcel, exportPdf, ExportColumn, ExportRow } from '../../../utils/hrExport';
import { Button } from '../../../components/ui/Button';
type ReportId = 'summary' | 'employee' | 'department' | 'paye' | 'napsa' | 'nhima' | 'employer' | 'advances' | 'loans' | 'schedule' | 'annual' | 'history' | 'variance' | 'register';
const reports: {
  id: ReportId;
  label: string;
  scope: 'run' | 'year' | 'employee' | 'none';
}[] = [{
  id: 'summary',
  label: 'Monthly Payroll Summary',
  scope: 'run'
}, {
  id: 'employee',
  label: 'Employee Salary Report',
  scope: 'run'
}, {
  id: 'department',
  label: 'Department Salary Report',
  scope: 'run'
}, {
  id: 'paye',
  label: 'PAYE Report',
  scope: 'run'
}, {
  id: 'napsa',
  label: 'NAPSA Report',
  scope: 'run'
}, {
  id: 'nhima',
  label: 'NHIMA Report',
  scope: 'run'
}, {
  id: 'employer',
  label: 'Employer Statutory Contributions',
  scope: 'run'
}, {
  id: 'schedule',
  label: 'Payroll Payment Schedule',
  scope: 'run'
}, {
  id: 'variance',
  label: 'Payroll Variance Report',
  scope: 'run'
}, {
  id: 'advances',
  label: 'Salary Advances Report',
  scope: 'none'
}, {
  id: 'loans',
  label: 'Employee Loan Balances',
  scope: 'none'
}, {
  id: 'annual',
  label: 'Annual Payroll Expenditure',
  scope: 'year'
}, {
  id: 'history',
  label: 'Individual Payroll History',
  scope: 'employee'
}, {
  id: 'register',
  label: 'Payslip Register',
  scope: 'year'
}];
const money = (key: string, label: string): ExportColumn => ({
  key,
  label,
  align: 'right'
});

/** Every report is derived from saved payroll runs, payslips and loans — nothing is estimated. */
export function PayrollReports() {
  const {
    payrollRuns,
    payslips,
    loans,
    employees
  } = useHrPayroll();
  const finalised = useMemo(() => payrollRuns.filter(r => r.status === 'Processed' || r.status === 'Paid').sort((a, b) => b.period.localeCompare(a.period)), [payrollRuns]);
  const years = Array.from(new Set(finalised.map(r => r.period.slice(0, 4)))).sort().reverse();
  const [reportId, setReportId] = useState<ReportId>('summary');
  const [runId, setRunId] = useState('');
  const [year, setYear] = useState(years[0] ?? String(new Date().getFullYear()));
  const [employeeId, setEmployeeId] = useState('');
  const report = reports.find(r => r.id === reportId)!;
  const run = finalised.find(r => r.id === runId) ?? finalised[0];
  const previousRun = run ? finalised.find(r => r.period < run.period) : undefined;
  const built = useMemo(() => buildReport(reportId, {
    run,
    previousRun,
    year,
    employeeId,
    finalised,
    payslips,
    loans,
    employees
  }), [reportId, run, previousRun, year, employeeId, finalised, payslips, loans, employees]);
  const subtitle = report.scope === 'run' && run ? `${formatPeriod(run.period)} · ${run.reference}` : report.scope === 'year' ? `Year ${year}` : report.scope === 'employee' ? employeeName(employees.find(e => e.id === employeeId)) : `As at ${formatDate(new Date().toISOString())}`;
  const file = `${report.label}_${subtitle}`;
  const needsRun = report.scope === 'run' && !run;
  return <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]" data-id="element-1520" data-mp="3ic6hi03a9ap6">
      <nav aria-label="Payroll reports" className="h-fit rounded-xl border border-slate-200 bg-white p-2 shadow-sm" data-id="element-1521" data-mp="2uoy7x5ytnfhc">
        {reports.map(r => <button key={r.id} type="button" onClick={() => setReportId(r.id)} aria-current={reportId === r.id ? 'page' : undefined} className={`block w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${reportId === r.id ? 'bg-blue-50 font-semibold text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-1522" data-mp="34dyebafnld5k">
            {r.label}
          </button>)}
      </nav>

      <section className="min-w-0 space-y-4" data-id="element-1523" data-mp="2klt8mkwlogse">
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1524" data-mp="2kmw1gydhgtay">
          <div className="mr-auto" data-id="element-1525" data-mp="2pkxg973vm6gm">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-1526" data-mp="2uk0m2trokgpn">{report.label}</h2>
            <p className="text-sm text-slate-500" data-id="element-1527" data-mp="2uhw7nhw5nzb4">{subtitle}</p>
          </div>
          {report.scope === 'run' && finalised.length > 0 && <label className="text-sm" data-id="element-1528" data-mp="33b0x1ijzl7yo"><span className="block text-xs font-medium text-slate-500" data-id="element-1529" data-mp="33bj0az52gmg3">Payroll</span>
              <select value={run?.id} onChange={e => setRunId(e.target.value)} className="mt-1 rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-1530" data-mp="389uaxc36r0zd">
                {finalised.map(r => <option key={r.id} value={r.id} data-id="element-1531" data-mp="3i9l9bouva04q">{formatPeriod(r.period)} · {r.reference}</option>)}
              </select>
            </label>}
          {report.scope === 'year' && <label className="text-sm" data-id="element-1532" data-mp="1nusgfxldbngh"><span className="block text-xs font-medium text-slate-500" data-id="element-1533" data-mp="1nw3insz60n5h">Year</span>
              <select value={year} onChange={e => setYear(e.target.value)} className="mt-1 rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-1534" data-mp="517ocky5ld31">
                {(years.length ? years : [year]).map(y => <option key={y} data-id="element-1535" data-mp="3scgu8ri4dwa5">{y}</option>)}
              </select>
            </label>}
          {report.scope === 'employee' && <label className="text-sm" data-id="element-1536" data-mp="3nc2iaydee26u"><span className="block text-xs font-medium text-slate-500" data-id="element-1537" data-mp="nz1w6vnjpiju">Employee</span>
              <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} className="mt-1 max-w-xs rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-1538" data-mp="nwpb49mhqs2k">
                <option value="" data-id="element-1539" data-mp="ewisyaful3x2">Select employee…</option>
                {[...employees].sort((a, b) => a.employeeNumber.localeCompare(b.employeeNumber)).map(e => <option key={e.id} value={e.id} data-id="element-1540" data-mp="2zp44k8gnx8cm">{e.employeeNumber} · {employeeName(e)}</option>)}
              </select>
            </label>}
          <div className="flex gap-2" data-id="element-1541" data-mp="2uo6mdvvnkeb4">
            <Button size="sm" variant="outline" disabled={built.rows.length === 0} onClick={() => exportPdf(file, report.label, subtitle, built.columns, built.rows, built.totals)} data-id="element-1542" data-mp="2w6wxj4xde1tr"><DownloadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-1543" data-mp="313nmkhr5k7rb" /> PDF</Button>
            <Button size="sm" variant="outline" disabled={built.rows.length === 0} onClick={() => exportExcel(file, `${report.label} — ${subtitle}`, built.columns, built.totals ? [...built.rows, built.totals] : built.rows)} data-id="element-1544" data-mp="2n5pajj0b0nez">Excel</Button>
            <Button size="sm" variant="outline" disabled={built.rows.length === 0} onClick={() => exportCsv(file, built.columns, built.totals ? [...built.rows, built.totals] : built.rows)} data-id="element-1545" data-mp="2i6vy7twr72ge">CSV</Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1546" data-mp="28i48lsf60u8n">
          {needsRun || built.rows.length === 0 ? <div className="py-14 text-center text-sm text-slate-500" data-id="element-1547" data-mp="1ujnq747xxfao">
              <FileSpreadsheetIcon className="mx-auto mb-2 h-9 w-9 text-slate-300" aria-hidden="true" data-id="element-1548" data-mp="1zfv0f97ddcyf" />
              {needsRun ? 'No processed payroll yet. Reports appear once payroll has been processed.' : report.scope === 'employee' && !employeeId ? 'Select an employee to see their payroll history.' : 'No records for this selection.'}
            </div> : <div className="overflow-x-auto" data-id="element-1549" data-mp="1ktfpqrmff0w5">
              <table className="min-w-full text-sm" data-id="element-1550" data-mp="1kvs8v83x7has">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-1551" data-mp="1ysf4n7k501f4">
                  <tr data-id="element-1552" data-mp="1ttufc36fb25g">{built.columns.map(c => <th key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1553" data-mp="1tsjby4gw3wev">{c.label}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100 tabular-nums" data-id="element-1554" data-mp="23li0jmcdzmyh">
                  {built.rows.map((row, i) => <tr key={i} data-id="element-1555" data-mp="2hhvjkzwkjwf2" data-mpi={i}>{built.columns.map(c => <td key={c.key} className={`whitespace-nowrap px-4 py-2.5 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1556" data-mp="2diwi5pa1qhsd">{display(row[c.key], c)}</td>)}</tr>)}
                </tbody>
                {built.totals && <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold tabular-nums" data-id="element-1557" data-mp="3ouud3tv9b5za">
                    <tr data-id="element-1558" data-mp="3tw0aw63rxjfo">{built.columns.map(c => <td key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`} data-id="element-1559" data-mp="3ttnpfiyt4qxv">{display(built.totals![c.key], c)}</td>)}</tr>
                  </tfoot>}
              </table>
            </div>}
        </div>
      </section>
    </div>;
}
function display(value: string | number | undefined, column: ExportColumn) {
  if (typeof value === 'number') return column.key === 'count' || column.key === 'employees' ? value : formatKwacha(value);
  return value === undefined || value === '' ? '' : value;
}
type BuildInput = {
  run?: PayrollRun;
  previousRun?: PayrollRun;
  year: string;
  employeeId: string;
  finalised: PayrollRun[];
  payslips: ReturnType<typeof useHrPayroll>['payslips'];
  loans: ReturnType<typeof useHrPayroll>['loans'];
  employees: ReturnType<typeof useHrPayroll>['employees'];
};
type Built = {
  columns: ExportColumn[];
  rows: ExportRow[];
  totals?: ExportRow;
};
const withTotals = (columns: ExportColumn[], rows: ExportRow[], label: string): Built => {
  const totals: ExportRow = {
    [columns[0].key]: label
  };
  columns.forEach(c => {
    if (c.align === 'right') totals[c.key] = c.key === 'count' || c.key === 'employees' ? rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0) : sumKwacha(rows.map(r => Number(r[c.key]) || 0));
  });
  return {
    columns,
    rows,
    totals
  };
};
function buildReport(id: ReportId, input: BuildInput): Built {
  const {
    run,
    previousRun,
    year,
    employeeId,
    finalised,
    payslips,
    loans,
    employees
  } = input;
  const lines = run?.lines ?? [];
  switch (id) {
    case 'summary':
      {
        if (!run) return {
          columns: [],
          rows: []
        };
        const t = run.totals;
        const columns = [{
          key: 'item',
          label: 'Item'
        }, money('amount', 'Amount (ZMW)')];
        const rows: ExportRow[] = [{
          item: 'Employees paid',
          amount: t.employees
        }, {
          item: 'Basic salaries',
          amount: t.basic
        }, {
          item: 'Allowances & other earnings',
          amount: t.allowances
        }, {
          item: 'Gross pay',
          amount: t.gross
        }, {
          item: 'PAYE',
          amount: t.paye
        }, {
          item: 'NAPSA (employee)',
          amount: t.napsaEmployee
        }, {
          item: 'NHIMA (employee)',
          amount: t.nhimaEmployee
        }, {
          item: 'Other deductions',
          amount: t.otherDeductions
        }, {
          item: 'Total deductions',
          amount: t.totalDeductions
        }, {
          item: 'NET PAY',
          amount: t.net
        }, {
          item: 'NAPSA (employer)',
          amount: t.napsaEmployer
        }, {
          item: 'NHIMA (employer)',
          amount: t.nhimaEmployer
        }, {
          item: 'Skills Development Levy',
          amount: t.sdl
        }, {
          item: "Workers' Compensation",
          amount: t.workersComp
        }, {
          item: 'TOTAL EMPLOYER COST',
          amount: t.employerCost
        }];
        return {
          columns: [{
            key: 'item',
            label: 'Item'
          }, {
            key: 'amount',
            label: 'Amount (ZMW)',
            align: 'right'
          }],
          rows: rows.map(r => r.item === 'Employees paid' ? {
            ...r,
            amount: String(r.amount)
          } : r)
        };
      }
    case 'employee':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'dept',
        label: 'Department'
      }, money('basic', 'Basic'), money('allow', 'Allowances'), money('gross', 'Gross'), money('ded', 'Deductions'), money('net', 'Net')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        dept: l.departmentName,
        basic: l.basic,
        allow: l.allowances,
        gross: l.gross,
        ded: l.totalDeductions,
        net: l.net
      })), 'TOTAL');
    case 'department':
      {
        const groups = new Map<string, typeof lines>();
        lines.forEach(l => groups.set(l.departmentName, [...(groups.get(l.departmentName) ?? []), l]));
        return withTotals([{
          key: 'dept',
          label: 'Department'
        }, {
          key: 'employees',
          label: 'Employees',
          align: 'right'
        }, money('gross', 'Gross'), money('paye', 'PAYE'), money('net', 'Net'), money('cost', 'Employer cost')], [...groups.entries()].sort().map(([dept, ls]) => ({
          dept,
          employees: ls.length,
          gross: sumKwacha(ls.map(l => l.gross)),
          paye: sumKwacha(ls.map(l => l.paye)),
          net: sumKwacha(ls.map(l => l.net)),
          cost: sumKwacha(ls.map(l => l.employerCost))
        })), 'TOTAL');
      }
    case 'paye':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'tpin',
        label: 'TPIN'
      }, money('taxable', 'Chargeable income'), money('paye', 'PAYE')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        tpin: l.tpin ?? '',
        taxable: l.taxable,
        paye: l.paye
      })), 'TOTAL');
    case 'napsa':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'member',
        label: 'NAPSA no.'
      }, money('ee', 'Employee'), money('er', 'Employer'), money('total', 'Total')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        member: l.napsaNumber ?? '',
        ee: l.napsaEmployee,
        er: l.napsaEmployer,
        total: sumKwacha([l.napsaEmployee, l.napsaEmployer])
      })), 'TOTAL');
    case 'nhima':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'member',
        label: 'NHIMA no.'
      }, money('ee', 'Employee'), money('er', 'Employer'), money('total', 'Total')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        member: l.nhimaNumber ?? '',
        ee: l.nhimaEmployee,
        er: l.nhimaEmployer,
        total: sumKwacha([l.nhimaEmployee, l.nhimaEmployer])
      })), 'TOTAL');
    case 'employer':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, money('napsa', 'NAPSA'), money('nhima', 'NHIMA'), money('sdl', 'SDL'), money('wc', "Workers' Comp"), money('total', 'Total')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        napsa: l.napsaEmployer,
        nhima: l.nhimaEmployer,
        sdl: l.sdl,
        wc: l.workersComp,
        total: sumKwacha([l.napsaEmployer, l.nhimaEmployer, l.sdl, l.workersComp])
      })), 'TOTAL');
    case 'schedule':
      return withTotals([{
        key: 'number',
        label: 'Employee ID'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'bank',
        label: 'Bank'
      }, {
        key: 'branch',
        label: 'Branch'
      }, {
        key: 'account',
        label: 'Account'
      }, money('net', 'Net pay')], lines.map(l => ({
        number: l.employeeNumber,
        name: l.name,
        bank: l.bankName ?? '',
        branch: l.bankBranch ?? '',
        account: maskAccount(l.bankAccountNumber),
        net: l.net
      })), 'TOTAL');
    case 'variance':
      {
        const prev = new Map((previousRun?.lines ?? []).map(l => [l.employeeId, l]));
        const ids = new Set([...lines.map(l => l.employeeId), ...prev.keys()]);
        const current = new Map(lines.map(l => [l.employeeId, l]));
        return withTotals([{
          key: 'name',
          label: 'Employee'
        }, {
          key: 'note',
          label: previousRun ? `vs ${formatPeriod(previousRun.period)}` : 'No earlier payroll'
        }, money('prevNet', 'Previous net'), money('net', 'Current net'), money('diff', 'Change')], [...ids].map(id => {
          const c = current.get(id);
          const p = prev.get(id);
          return {
            name: c?.name ?? p?.name ?? id,
            note: !p ? 'New this month' : !c ? 'Not paid this month' : '',
            prevNet: p?.net ?? 0,
            net: c?.net ?? 0,
            diff: sumKwacha([c?.net ?? 0, -(p?.net ?? 0)])
          };
        }).filter(r => r.diff !== 0 || r.note).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff)), 'TOTAL');
      }
    case 'advances':
    case 'loans':
      {
        const type = id === 'advances' ? 'Salary Advance' : 'Loan';
        return withTotals([{
          key: 'name',
          label: 'Employee'
        }, {
          key: 'issued',
          label: 'Issued'
        }, {
          key: 'status',
          label: 'Status'
        }, money('amount', 'Amount'), money('instalment', 'Monthly'), money('recovered', 'Recovered'), money('balance', 'Balance')], loans.filter(l => l.type === type).map(l => ({
          name: employeeName(employees.find(e => e.id === l.employeeId)),
          issued: formatDate(l.issueDate),
          status: l.status,
          amount: l.amount,
          instalment: l.monthlyInstalment,
          recovered: sumKwacha(l.repayments.map(r => r.amount)),
          balance: l.balance
        })), 'TOTAL');
      }
    case 'annual':
      return withTotals([{
        key: 'month',
        label: 'Month'
      }, {
        key: 'employees',
        label: 'Employees',
        align: 'right'
      }, money('gross', 'Gross'), money('paye', 'PAYE'), money('napsa', 'NAPSA (all)'), money('nhima', 'NHIMA (all)'), money('net', 'Net'), money('cost', 'Employer cost')], finalised.filter(r => r.period.startsWith(year)).sort((a, b) => a.period.localeCompare(b.period)).map(r => ({
        month: formatPeriod(r.period),
        employees: r.totals.employees,
        gross: r.totals.gross,
        paye: r.totals.paye,
        napsa: sumKwacha([r.totals.napsaEmployee, r.totals.napsaEmployer]),
        nhima: sumKwacha([r.totals.nhimaEmployee, r.totals.nhimaEmployer]),
        net: r.totals.net,
        cost: r.totals.employerCost
      })), 'YEAR TOTAL');
    case 'history':
      if (!employeeId) return {
        columns: [],
        rows: []
      };
      return withTotals([{
        key: 'month',
        label: 'Month'
      }, {
        key: 'slip',
        label: 'Payslip'
      }, {
        key: 'status',
        label: 'Status'
      }, money('gross', 'Gross'), money('paye', 'PAYE'), money('ded', 'Deductions'), money('net', 'Net')], payslips.filter(p => p.employeeId === employeeId && p.status !== 'Void').sort((a, b) => a.period.localeCompare(b.period)).map(p => ({
        month: formatPeriod(p.period),
        slip: p.payslipNumber,
        status: p.status,
        gross: p.data.gross,
        paye: p.data.paye,
        ded: p.data.totalDeductions,
        net: p.data.net
      })), 'TOTAL');
    case 'register':
      return withTotals([{
        key: 'slip',
        label: 'Payslip no.'
      }, {
        key: 'month',
        label: 'Month'
      }, {
        key: 'name',
        label: 'Employee'
      }, {
        key: 'status',
        label: 'Status'
      }, money('net', 'Net')], payslips.filter(p => p.period.startsWith(year)).sort((a, b) => a.payslipNumber.localeCompare(b.payslipNumber)).map(p => ({
        slip: p.payslipNumber,
        month: formatPeriod(p.period),
        name: p.data.name,
        status: p.status,
        net: p.status === 'Void' ? 0 : p.data.net
      })), 'TOTAL');
  }
}